/**
 * 外部アセット不要の Web Audio 合成サウンド。
 * BGM・環境音・SFX をすべてコード生成。
 */
export class AudioManager {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private bgmGain: GainNode | null = null;
  private sfxGain: GainNode | null = null;
  private ambientGain: GainNode | null = null;
  private bgmNodes: AudioNode[] = [];
  private ambientNodes: AudioNode[] = [];
  private started = false;

  bgmVolume = 0.4;
  sfxVolume = 0.7;

  async unlock(): Promise<void> {
    if (!this.ctx) {
      this.ctx = new AudioContext();
      this.master = this.ctx.createGain();
      this.master.gain.value = 1;
      this.master.connect(this.ctx.destination);

      this.bgmGain = this.ctx.createGain();
      this.bgmGain.gain.value = this.bgmVolume;
      this.bgmGain.connect(this.master);

      this.sfxGain = this.ctx.createGain();
      this.sfxGain.gain.value = this.sfxVolume;
      this.sfxGain.connect(this.master);

      this.ambientGain = this.ctx.createGain();
      this.ambientGain.gain.value = 0.25;
      this.ambientGain.connect(this.master);
    }
    if (this.ctx.state === 'suspended') await this.ctx.resume();
  }

  setBgmVolume(v: number): void {
    this.bgmVolume = v;
    if (this.bgmGain) this.bgmGain.gain.value = v;
  }

  setSfxVolume(v: number): void {
    this.sfxVolume = v;
    if (this.sfxGain) this.sfxGain.gain.value = v;
  }

  startAmbience(): void {
    if (!this.ctx || !this.ambientGain || this.started) return;
    this.started = true;

    // 低いうねり（雨・遠い風）
    const bufferSize = this.ctx.sampleRate * 2;
    const noiseBuffer = this.ctx.createBuffer(1, bufferSize, this.ctx.sampleRate);
    const data = noiseBuffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) data[i] = Math.random() * 2 - 1;

    const noise = this.ctx.createBufferSource();
    noise.buffer = noiseBuffer;
    noise.loop = true;
    const filter = this.ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 400;
    const g = this.ctx.createGain();
    g.gain.value = 0.15;
    noise.connect(filter);
    filter.connect(g);
    g.connect(this.ambientGain);
    noise.start();
    this.ambientNodes.push(noise, filter, g);

    // 遠い低音ドローン
    const osc = this.ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = 55;
    const og = this.ctx.createGain();
    og.gain.value = 0.04;
    osc.connect(og);
    og.connect(this.ambientGain);
    osc.start();
    this.ambientNodes.push(osc, og);

    this.startBgm();
  }

  private startBgm(): void {
    if (!this.ctx || !this.bgmGain) return;
    // ゆっくりしたマイナーコード進行風パッド
    const notes = [110, 130.81, 164.81, 196]; // A2 C3 E3 G3
    notes.forEach((freq, i) => {
      const osc = this.ctx!.createOscillator();
      osc.type = i % 2 === 0 ? 'sine' : 'triangle';
      osc.frequency.value = freq;
      const g = this.ctx!.createGain();
      g.gain.value = 0.03;
      // LFO
      const lfo = this.ctx!.createOscillator();
      lfo.frequency.value = 0.05 + i * 0.02;
      const lfoG = this.ctx!.createGain();
      lfoG.gain.value = 0.015;
      lfo.connect(lfoG);
      lfoG.connect(g.gain);
      osc.connect(g);
      g.connect(this.bgmGain!);
      osc.start();
      lfo.start();
      this.bgmNodes.push(osc, g, lfo, lfoG);
    });
  }

  private tone(
    freq: number,
    duration: number,
    type: OscillatorType = 'sine',
    volume = 0.2,
    slideTo?: number,
  ): void {
    if (!this.ctx || !this.sfxGain) return;
    const t = this.ctx.currentTime;
    const osc = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, t);
    if (slideTo !== undefined) {
      osc.frequency.exponentialRampToValueAtTime(Math.max(1, slideTo), t + duration);
    }
    g.gain.setValueAtTime(volume, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + duration);
    osc.connect(g);
    g.connect(this.sfxGain);
    osc.start(t);
    osc.stop(t + duration + 0.02);
  }

  private noiseBurst(duration: number, volume = 0.15, freq = 800): void {
    if (!this.ctx || !this.sfxGain) return;
    const t = this.ctx.currentTime;
    const len = Math.floor(this.ctx.sampleRate * duration);
    const buffer = this.ctx.createBuffer(1, len, this.ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
    const src = this.ctx.createBufferSource();
    src.buffer = buffer;
    const filter = this.ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.value = freq;
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(volume, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + duration);
    src.connect(filter);
    filter.connect(g);
    g.connect(this.sfxGain);
    src.start(t);
  }

  playFootstep(): void {
    this.noiseBurst(0.08, 0.12, 300);
  }

  playPickup(): void {
    this.tone(660, 0.12, 'sine', 0.18);
    this.tone(990, 0.18, 'sine', 0.12);
  }

  playClick(): void {
    this.tone(440, 0.05, 'square', 0.06);
  }

  playDoor(): void {
    this.noiseBurst(0.25, 0.18, 180);
    this.tone(120, 0.3, 'triangle', 0.1, 80);
  }

  playSuccess(): void {
    this.tone(523, 0.12, 'sine', 0.15);
    setTimeout(() => this.tone(659, 0.12, 'sine', 0.15), 100);
    setTimeout(() => this.tone(784, 0.25, 'sine', 0.18), 200);
  }

  playError(): void {
    this.tone(200, 0.2, 'sawtooth', 0.1, 100);
  }

  playClear(): void {
    const melody = [523, 659, 784, 1046];
    melody.forEach((f, i) => {
      setTimeout(() => this.tone(f, 0.4, 'sine', 0.2), i * 180);
    });
  }

  playDrawer(): void {
    this.noiseBurst(0.2, 0.14, 400);
    this.tone(180, 0.15, 'triangle', 0.08);
  }

  playPower(): void {
    this.tone(80, 0.5, 'sawtooth', 0.12, 200);
    this.noiseBurst(0.4, 0.1, 600);
  }

  playPianoNote(note: string): void {
    const map: Record<string, number> = {
      C: 261.63,
      D: 293.66,
      E: 329.63,
      F: 349.23,
      G: 392.0,
      A: 440.0,
      B: 493.88,
    };
    this.tone(map[note] ?? 300, 0.35, 'triangle', 0.16);
  }

  playFire(): void {
    this.noiseBurst(0.35, 0.12, 500);
    this.tone(180, 0.25, 'sawtooth', 0.06, 90);
  }
}
